/**
 * MeCycleAnalysis — Cycle analyzer page
 *   /me/health/cycle/analysis
 *  Reference layout: Average period + Average cycle cards, Add Period CTA,
 *  and a per-cycle timeline history grouped by year.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import { ArrowLeft, Drop, Plus, X, CircleNotch } from "@phosphor-icons/react";

const FILTERS = [
  { id: "all",       label: "All" },
  { id: "period",    label: "Period" },
  { id: "ovulation", label: "Ovulation" },
  { id: "fertile",   label: "Fertile" },
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtRange(startISO, endISO) {
  const s = new Date(startISO + "T00:00:00");
  const e = new Date(endISO + "T00:00:00");
  const mon = (d) => d.toLocaleDateString(undefined, { month: "short" });
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${mon(s)} ${s.getDate()} – ${mon(e)} ${e.getDate()}`;
  }
  return `${mon(s)} ${s.getDate()} – ${mon(e)} ${e.getDate()}`;
}
function yearOf(iso) { return iso.slice(0, 4); }

export default function MeCycleAnalysis() {
  const [lovedOnes, setLovedOnes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const nav = useNavigate();

  const loadLO = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = (r.data.loved_ones || []).filter(l => {
        const kind = (l.kind || "self").toLowerCase();
        const gender = (l.gender || "").toLowerCase();
        if (["male", "other", "others"].includes(gender)) return false;
        if (l.age != null && l.age < 13) return false;
        if (kind && !["woman", "pregnant", "self"].includes(kind)) return false;
        return gender === "female" || kind === "woman" || kind === "pregnant" || kind === "self";
      });
      setLovedOnes(list);
      if (list[0] && !selectedId) setSelectedId(list[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const load = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const r = await custApi.get(`/me/cycle/analysis?loved_one_id=${selectedId}`);
      setAnalysis(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadLO(); }, []);
  // eslint-disable-next-line
  useEffect(() => { load(); }, [selectedId]);

  const groupedByYear = useMemo(() => {
    const list = analysis?.cycles || [];
    const groups = {};
    for (const c of list) {
      const y = yearOf(c.start);
      (groups[y] = groups[y] || []).push(c);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [analysis]);

  const avgPeriod = analysis?.avg_period_length || 0;
  const avgCycle = analysis?.avg_cycle_length || 0;
  const total = analysis?.cycles_recorded || 0;

  if (!selectedId && lovedOnes.length === 0) {
    return (
      <div className="min-h-screen bg-[#F5F0F5] pb-24" data-testid="cycle-analysis">
        <TopBar onBack={() => nav(-1)} />
        <div className="p-6 text-center text-sm text-gray-500">
          Add a Woman/Self profile to see cycle analysis.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0F5] pb-28" data-testid="cycle-analysis">
      <TopBar onBack={() => nav(-1)} />

      {/* Loved-one picker if multiple eligible */}
      {lovedOnes.length > 1 && (
        <div className="px-4 pt-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {lovedOnes.map(lo => {
              const on = lo.id === selectedId;
              return (
                <button key={lo.id} onClick={() => setSelectedId(lo.id)}
                        data-testid={`ca-picker-${lo.id}`}
                        className={`h-9 px-3 shrink-0 rounded-full border text-xs font-medium ${
                          on ? "bg-[#EC4899] text-white border-[#EC4899]"
                             : "bg-white text-[#1A1A1A] border-[#E5E2D9]"}`}>
                  {lo.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Analysis stat card */}
      <div className="mx-4 mt-4 rounded-3xl bg-white p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
           data-testid="ca-stats-card">
        <div className="font-display font-black text-[22px] leading-tight text-[#111827]">
          Cycle analysis
        </div>
        <div className="text-[13px] text-gray-500 mt-0.5">
          Based on last {Math.min(total, 6)} cycles
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <StatTile
            testid="ca-stat-period"
            bg="#FCE7F3" fg="#EC4899"
            value={avgPeriod} unit="Days" label="Average period"
            icon={<Drop size={22} weight="fill" color="#EC4899" />}
          />
          <StatTile
            testid="ca-stat-cycle"
            bg="#FEF3C7" fg="#D97706"
            value={avgCycle} unit="Days" label="Average cycle"
            icon={<div className="w-6 h-6 rounded-full border-[3px] border-[#F59E0B] border-r-transparent" />}
          />
        </div>

        <button onClick={() => setShowAdd(true)}
                data-testid="ca-add-period"
                className="mt-4 w-full h-13 rounded-2xl bg-[#EC4899] hover:bg-[#DB2777] text-white text-[15px] font-bold inline-flex items-center justify-center gap-2 shadow-[0_6px_20px_rgba(236,72,153,0.35)] py-3.5">
          <Plus size={18} weight="bold" /> Add Period
        </button>
      </div>

      {/* History card */}
      <div className="mx-4 mt-4 rounded-3xl bg-white p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
           data-testid="ca-history-card">
        <div className="flex items-end justify-between">
          <div>
            <div className="font-display font-black text-[22px] leading-tight text-[#111827]">
              History
            </div>
            <div className="text-[13px] text-gray-500 mt-0.5">
              {total} {total === 1 ? "cycle" : "cycles"} logged
            </div>
          </div>
          <div className="text-[13px] text-gray-400 font-medium">Prediction</div>
        </div>

        {/* Filter chips */}
        <div className="mt-3 flex gap-2 overflow-x-auto -mx-1 px-1">
          {FILTERS.map(f => {
            const on = f.id === filter;
            return (
              <button key={f.id} onClick={() => setFilter(f.id)}
                      data-testid={`ca-filter-${f.id}`}
                      className={`h-9 px-4 shrink-0 rounded-full text-[13px] font-semibold transition-colors ${
                        on ? "bg-[#EC4899] text-white"
                           : "bg-[#F3F4F6] text-[#5C5A55]"}`}>
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Cycle rows grouped by year */}
        <div className="mt-4 space-y-5">
          {loading && (
            <div className="py-8 flex items-center justify-center text-gray-400">
              <CircleNotch size={22} className="animate-spin"/>
            </div>
          )}
          {!loading && total === 0 && (
            <div className="py-8 text-center text-sm text-gray-500">
              No cycles yet. Tap “Add Period” to log your first one.
            </div>
          )}
          {!loading && groupedByYear.map(([year, cyc]) => (
            <div key={year}>
              <div className="font-display font-black text-[22px] text-[#111827] mb-2">
                {year}
              </div>
              <div className="space-y-4">
                {cyc.map((c, idx) => (
                  <CycleRow key={`${c.start}-${idx}`}
                            cycle={c}
                            filter={filter}
                            avgCycle={avgCycle}/>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showAdd && (
        <AddPeriodSheet
          lovedOneId={selectedId}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

function TopBar({ onBack }) {
  return (
    <div className="sticky top-0 z-10 bg-[#F5F0F5]/95 backdrop-blur">
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={onBack}
                data-testid="ca-back"
                className="w-9 h-9 rounded-full bg-white border border-gray-100 inline-flex items-center justify-center">
          <ArrowLeft size={16} weight="bold" className="text-gray-700"/>
        </button>
        <div className="flex-1 text-center font-display font-black text-[18px] tracking-tight text-[#111827] pr-9">
          Cycle analysis
        </div>
      </div>
    </div>
  );
}

function StatTile({ testid, bg, fg, value, unit, label, icon }) {
  return (
    <div className="rounded-2xl p-4 relative overflow-hidden"
         style={{ backgroundColor: bg }} data-testid={testid}>
      <div className="absolute top-3 right-3">{icon}</div>
      <div className="font-display font-black text-[26px] leading-tight" style={{ color: fg }}>
        {value} <span className="text-[15px] font-bold">{unit}</span>
      </div>
      <div className="text-[13px] mt-1 font-semibold" style={{ color: fg, opacity: 0.9 }}>
        {label}
      </div>
    </div>
  );
}

/* ───── Cycle history row with timeline bar ───── */
function CycleRow({ cycle, filter, avgCycle }) {
  const totalDays = Math.max(avgCycle || 28, cycle.cycle_length || 28);
  const startD = new Date(cycle.start + "T00:00:00");
  const endD = new Date(cycle.end + "T00:00:00");
  const ovD = new Date(cycle.ovulation_day + "T00:00:00");
  const fertS = new Date(cycle.fertility_start + "T00:00:00");
  const fertE = new Date(cycle.fertility_end + "T00:00:00");
  const dayFromStart = (d) => Math.max(0, Math.round((d - startD) / 86400000));

  const periodPct = Math.max(3, ((cycle.length_days || 1) / totalDays) * 100);
  const fertLeftPct = (dayFromStart(fertS) / totalDays) * 100;
  const fertWidthPct = Math.max(6, ((dayFromStart(fertE) - dayFromStart(fertS) + 1) / totalDays) * 100);
  const ovLeftPct = (dayFromStart(ovD) / totalDays) * 100;

  // Filter: dim non-matching pieces
  const showPeriod = filter === "all" || filter === "period";
  const showFert   = filter === "all" || filter === "fertile";
  const showOv     = filter === "all" || filter === "ovulation";

  return (
    <div data-testid={`ca-cycle-${cycle.start}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="font-display font-bold text-[15px] text-[#111827]">
          {fmtRange(cycle.start, cycle.end)}
        </div>
        <div className="text-[15px] font-bold text-[#111827]">
          {cycle.cycle_length}
        </div>
      </div>
      <div className="relative h-6">
        {/* Base track */}
        <div className="absolute inset-y-0 left-0 right-0 rounded-full bg-[#F3E9EB]"/>
        {/* Fertile band (light pink) */}
        {showFert && (
          <div className="absolute inset-y-0 rounded-full bg-[#FBD5DE]"
               style={{ left: `${fertLeftPct}%`, width: `${fertWidthPct}%` }}/>
        )}
        {/* Period pill (pink) */}
        {showPeriod && (
          <div className="absolute inset-y-0 left-0 rounded-full bg-[#EC4899] inline-flex items-center pl-2.5"
               style={{ width: `${periodPct}%` }}>
            <span className="text-white text-[12px] font-bold">{cycle.length_days}</span>
          </div>
        )}
        {/* Ovulation dot (yellow) */}
        {showOv && (
          <div className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center"
               style={{ left: `calc(${ovLeftPct}% - 12px)` }}>
            <div className="w-6 h-6 rounded-full bg-[#F59E0B] inline-flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-white"/>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───── Add Period bottom sheet ───── */
function AddPeriodSheet({ lovedOneId, onClose, onSaved }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!startDate) return toast.error("Pick the period start date");
    if (!endDate) return toast.error("Pick the period end date");
    if (endDate < startDate) return toast.error("End date must be on or after start date");
    setBusy(true);
    try {
      await custApi.post("/me/cycle/period", {
        loved_one_id: lovedOneId,
        start_date: startDate,
        end_date: endDate,
      });
      toast.success("Period added");
      onSaved && onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
         onClick={onClose} data-testid="ca-add-sheet">
      <div className="w-full max-w-[480px] bg-white rounded-t-3xl p-5 pb-8"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-black text-[18px] text-[#111827]">
            Add Period
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 inline-flex items-center justify-center"
                  data-testid="ca-add-close">
            <X size={14} weight="bold"/>
          </button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">
              Period start
            </div>
            <input type="date" value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={todayISO()}
                    data-testid="ca-add-start"
                    className="w-full h-12 px-3 border border-[#E5E2D9] rounded-xl text-sm"/>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">
              Period end
            </div>
            <input type="date" value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    max={todayISO()}
                    min={startDate || undefined}
                    data-testid="ca-add-end"
                    className="w-full h-12 px-3 border border-[#E5E2D9] rounded-xl text-sm"/>
          </label>
        </div>
        <button onClick={save} disabled={busy}
                data-testid="ca-add-save"
                className="mt-4 w-full h-12 rounded-2xl bg-[#EC4899] hover:bg-[#DB2777] disabled:opacity-50 text-white text-[15px] font-bold">
          {busy ? "Saving…" : "Save Period"}
        </button>
      </div>
    </div>
  );
}
